import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  useTheme,
} from "@mui/material";
import { MergeType as MergeIcon } from "@mui/icons-material";
import { tokens } from "../../../../theme";
import type { VenueData } from "../../types/venues.types";

interface VenueMergeDialogProps {
  open: boolean;
  onClose: () => void;
  primaryVenue: VenueData;
  secondaryVenue: VenueData | null;
  secondaryLoading: boolean;
  onMerge: (fieldSelections: Record<string, "primary" | "secondary">) => void;
  merging: boolean;
}

interface FieldGroup {
  label: string;
  fields: { key: string; label: string }[];
}

const FIELD_GROUPS: FieldGroup[] = [
  {
    label: "Basic Information",
    fields: [
      { key: "name", label: "Name" },
      { key: "venue_type", label: "Venue Type" },
      { key: "capacity", label: "Capacity" },
      { key: "address_one", label: "Address Line 1" },
      { key: "address_two", label: "Address Line 2" },
      { key: "suburb", label: "Suburb" },
      { key: "city", label: "City" },
      { key: "location", label: "Location" },
      { key: "country", label: "Country" },
      { key: "time_zone", label: "Time Zone" },
      { key: "notes", label: "Notes" },
      { key: "travel_duration", label: "Travel Duration" },
    ],
  },
  {
    label: "Power",
    fields: [
      { key: "has_power", label: "Has Power" },
      { key: "power_type", label: "Power Type" },
      { key: "power_outage_rate", label: "Power Outage Rate" },
      { key: "power_socket_type", label: "Power Socket Type" },
      { key: "power_backup", label: "Power Backup" },
      { key: "backup_power_capacity", label: "Backup Power Capacity" },
      {
        key: "power_distance_from_livestream_desk",
        label: "Power Distance from Desk",
      },
    ],
  },
  {
    label: "Internet",
    fields: [
      { key: "internet_service_provider", label: "ISP" },
      { key: "wifi_name", label: "WiFi Name" },
      { key: "wifi_password", label: "WiFi Password" },
      { key: "internet_upload_speed", label: "Upload Speed (Mbps)" },
      { key: "router_accessibility", label: "Router Accessible" },
      {
        key: "router_distance_from_livestream",
        label: "Router Distance from Desk",
      },
    ],
  },
  {
    label: "PA System",
    fields: [
      { key: "has_pa_system", label: "Has PA System" },
      { key: "pa_system_position", label: "PA Position" },
      {
        key: "pa_system_distance_from_livestream",
        label: "PA Distance from Desk",
      },
      { key: "pa_system_provider", label: "PA Provider" },
      { key: "other_pa_system_providers", label: "Other PA Providers" },
      {
        key: "other_pa_system_service_provider",
        label: "Other PA Service Provider",
      },
      { key: "pa_system_contact_name", label: "PA Contact Name" },
      { key: "pa_system_contact_phone", label: "PA Contact Phone" },
      { key: "pa_system_contact_email", label: "PA Contact Email" },
    ],
  },
  {
    label: "Camera & Media",
    fields: [
      { key: "number_of_cameras", label: "Number of Cameras" },
      { key: "camera_positions", label: "Camera Positions" },
    ],
  },
];

const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

export const VenueMergeDialog: React.FC<VenueMergeDialogProps> = ({
  open,
  onClose,
  primaryVenue,
  secondaryVenue,
  secondaryLoading,
  onMerge,
  merging,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Initialize field selections: default to primary
  const [fieldSelections, setFieldSelections] = useState<
    Record<string, "primary" | "secondary">
  >({});
  const [confirmStep, setConfirmStep] = useState(false);

  // Determine which venue has a higher completeness score
  const primaryIsStronger =
    primaryVenue.completeness_score >=
    (secondaryVenue?.completeness_score ?? 0);

  // Initialize selections when secondary venue loads
  useEffect(() => {
    if (secondaryVenue) {
      const initialSelections: Record<string, "primary" | "secondary"> = {};
      for (const group of FIELD_GROUPS) {
        for (const field of group.fields) {
          // Default: prefer the venue with higher completeness score
          // If primary is stronger, default to primary; otherwise secondary
          // But also consider: if primary field is empty and secondary has data, prefer secondary
          const primaryVal = (primaryVenue as unknown as Record<string, unknown>)[
            field.key
          ];
          const secondaryVal = (secondaryVenue as unknown as Record<string, unknown>)[
            field.key
          ];
          const primaryEmpty =
            primaryVal === null || primaryVal === undefined || primaryVal === "";
          const secondaryEmpty =
            secondaryVal === null ||
            secondaryVal === undefined ||
            secondaryVal === "";

          if (primaryEmpty && !secondaryEmpty) {
            initialSelections[field.key] = "secondary";
          } else if (!primaryEmpty && secondaryEmpty) {
            initialSelections[field.key] = "primary";
          } else {
            initialSelections[field.key] = primaryIsStronger
              ? "primary"
              : "secondary";
          }
        }
      }
      setFieldSelections(initialSelections);
      setConfirmStep(false);
    }
  }, [secondaryVenue, primaryVenue, primaryIsStronger]);

  const handleSelectionChange = (
    fieldKey: string,
    value: "primary" | "secondary",
  ) => {
    setFieldSelections((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleMerge = () => {
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }
    onMerge(fieldSelections);
  };

  const handleClose = () => {
    setConfirmStep(false);
    onClose();
  };

  // Count how many fields are selected from each venue
  const selectionCounts = useMemo(() => {
    const counts = { primary: 0, secondary: 0 };
    for (const val of Object.values(fieldSelections)) {
      counts[val]++;
    }
    return counts;
  }, [fieldSelections]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { maxHeight: "90vh" } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MergeIcon />
          <Typography variant="h5" fontWeight="bold">
            Merge Venues
          </Typography>
        </Box>
        {secondaryVenue && (
          <Box display="flex" gap={2} mt={1}>
            <Chip
              label={`Primary: ${primaryVenue.name} (Score: ${primaryVenue.completeness_score})`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Secondary: ${secondaryVenue.name} (Score: ${secondaryVenue.completeness_score})`}
              color="secondary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {secondaryLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !secondaryVenue ? (
          <Typography>Secondary venue not found.</Typography>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 3 }}>
              All bookings, documents, layouts, site visits, routes, and contacts
              from <strong>{secondaryVenue.name}</strong> will be transferred to{" "}
              <strong>{primaryVenue.name}</strong>. The secondary venue will be
              permanently deleted. This action cannot be undone.
            </Alert>

            <Box display="flex" justifyContent="center" gap={2} mb={2}>
              <Typography variant="body2">
                Fields from Primary:{" "}
                <strong>{selectionCounts.primary}</strong>
              </Typography>
              <Typography variant="body2">
                Fields from Secondary:{" "}
                <strong>{selectionCounts.secondary}</strong>
              </Typography>
            </Box>

            {FIELD_GROUPS.map((group) => (
              <Box key={group.label} mb={3}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    backgroundColor: colors.primary[800],
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  {group.label}
                </Typography>

                {/* Header row */}
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr 1fr"
                  gap={1}
                  px={2}
                  py={1}
                  sx={{ borderBottom: `1px solid ${colors.grey[800]}` }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    Field
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" align="center">
                    Primary Value
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" align="center">
                    Secondary Value
                  </Typography>
                </Box>

                {group.fields.map((field) => {
                  const primaryVal = (
                    primaryVenue as unknown as Record<string, unknown>
                  )[field.key];
                  const secondaryVal = (
                    secondaryVenue as unknown as Record<string, unknown>
                  )[field.key];
                  const selection = fieldSelections[field.key] || "primary";

                  return (
                    <Box
                      key={field.key}
                      display="grid"
                      gridTemplateColumns="1fr 1fr 1fr"
                      gap={1}
                      px={2}
                      py={0.5}
                      alignItems="center"
                      sx={{
                        "&:hover": {
                          backgroundColor: colors.primary[800],
                        },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {field.label}
                      </Typography>

                      <RadioGroup
                        row
                        value={selection}
                        onChange={(e) =>
                          handleSelectionChange(
                            field.key,
                            e.target.value as "primary" | "secondary",
                          )
                        }
                        sx={{ justifyContent: "center" }}
                      >
                        <FormControlLabel
                          value="primary"
                          control={<Radio size="small" />}
                          label={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  selection === "primary" ? "bold" : "normal",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatFieldValue(primaryVal)}
                            </Typography>
                          }
                        />
                      </RadioGroup>

                      <RadioGroup
                        row
                        value={selection}
                        onChange={(e) =>
                          handleSelectionChange(
                            field.key,
                            e.target.value as "primary" | "secondary",
                          )
                        }
                        sx={{ justifyContent: "center" }}
                      >
                        <FormControlLabel
                          value="secondary"
                          control={<Radio size="small" />}
                          label={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  selection === "secondary" ? "bold" : "normal",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatFieldValue(secondaryVal)}
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </Box>
                  );
                })}
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}

            {confirmStep && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Are you sure you want to merge these venues? This will
                  permanently delete &quot;{secondaryVenue.name}&quot; and
                  transfer all its related records to &quot;{primaryVenue.name}
                  &quot;.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={merging}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={confirmStep ? "error" : "primary"}
          onClick={handleMerge}
          disabled={merging || secondaryLoading || !secondaryVenue}
          startIcon={
            merging ? <CircularProgress size={18} /> : <MergeIcon />
          }
        >
          {merging
            ? "Merging..."
            : confirmStep
              ? "Confirm Merge"
              : "Merge Venues"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VenueMergeDialog;
